/**
 * Lightweight analytics layer for the marketing site.
 *
 * Wraps Google Analytics 4 (gtag.js) and, optionally, the Meta (Facebook)
 * Pixel behind a tiny typed API so the rest of the app never touches the
 * global `gtag`/`fbq` objects directly. Both providers are opt-in via env
 * vars and no-op when unconfigured:
 *
 *   REACT_APP_GA_MEASUREMENT_ID   e.g. "G-XXXXXXXXXX"   (GA4)
 *   REACT_APP_META_PIXEL_ID       e.g. "123456789012345" (optional)
 *
 * Nothing loads or fires during react-snap pre-rendering (the headless
 * crawler reports `navigator.userAgent === "ReactSnap"`), so the statically
 * generated HTML stays free of tracking scripts and no phantom hits are sent
 * at build time. Adding a provider also requires widening `connect-src`/
 * `script-src` in `public/_headers`; see the comment there.
 */

// GA4 Measurement ID. A measurement ID is not a secret (it ships in the client
// bundle regardless), so the production property is baked in as the default to
// keep tracking live without any deploy-time env config. Set the env var to
// override it: e.g. to point a staging build at a separate property, or to ''
// to disable GA locally.
const GA_MEASUREMENT_ID =
  process.env.REACT_APP_GA_MEASUREMENT_ID ?? 'G-JEWZ9MRV87';
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: ((...args: unknown[]) => void) & { callMethod?: (...args: unknown[]) => void };
    _fbq?: unknown;
  }
}

let initialized = false;

/**
 * True during react-snap pre-rendering or in a non-browser context. In either
 * case we must not inject scripts or emit events.
 */
const isPrerenderOrServer = (): boolean =>
  typeof window === 'undefined' ||
  typeof document === 'undefined' ||
  navigator.userAgent === 'ReactSnap';

const gaEnabled = (): boolean => Boolean(GA_MEASUREMENT_ID);
const pixelEnabled = (): boolean => Boolean(META_PIXEL_ID);

/** Inject the GA4 gtag.js loader and bootstrap the data layer. */
const loadGa = (): void => {
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // gtag relies on `arguments`, so this cannot be an arrow function.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  // We send page_view manually on every route change (SPA), so disable the
  // automatic one to avoid double-counting the initial load.
  window.gtag('config', GA_MEASUREMENT_ID as string, { send_page_view: false });
};

/** Inject the Meta Pixel snippet. */
const loadPixel = (): void => {
  /* eslint-disable */
  (function (f: any, b: Document, e: string, v: string) {
    if (f.fbq) return;
    const n: any = (f.fbq = function () {
      n.callMethod
        ? n.callMethod.apply(n, arguments)
        : n.queue.push(arguments);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s.parentNode!.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq!('init', META_PIXEL_ID as string);
};

/**
 * Load the configured analytics providers exactly once. Safe to call
 * repeatedly (subsequent calls are no-ops) and safe to call during
 * pre-rendering (does nothing).
 */
export const initAnalytics = (): void => {
  if (initialized || isPrerenderOrServer()) return;
  if (!gaEnabled() && !pixelEnabled()) return;
  initialized = true;

  if (gaEnabled()) loadGa();
  if (pixelEnabled()) loadPixel();
};

/**
 * Record a SPA page view. Call on every route change; it lazily initialises
 * the providers so the first navigation is captured too.
 */
export const trackPageView = (path: string): void => {
  if (isPrerenderOrServer()) return;
  initAnalytics();
  if (!initialized) return;

  if (gaEnabled() && window.gtag) {
    window.gtag('event', 'page_view', { page_path: path, page_location: window.location.href });
  }
  if (pixelEnabled() && window.fbq) {
    window.fbq('track', 'PageView');
  }
};

/**
 * Fire a custom event to every configured provider. `params` maps to GA4
 * event params; the same payload is forwarded to the Pixel as a custom event.
 */
export const trackEvent = (
  name: string,
  params: Record<string, unknown> = {}
): void => {
  if (isPrerenderOrServer()) return;
  initAnalytics();
  if (!initialized) return;

  if (gaEnabled() && window.gtag) {
    window.gtag('event', name, params);
  }
  if (pixelEnabled() && window.fbq) {
    window.fbq('trackCustom', name, params);
  }
};

// --- Named conversion events -------------------------------------------------
// One helper per lead-generating action so call sites stay declarative and the
// event names/params never drift.

/** Contact form successfully submitted. Also reports a Pixel `Lead`. */
export const trackContactSubmit = (): void => {
  trackEvent('contact_form_submit', { form: 'contact' });
  if (pixelEnabled() && window.fbq) {
    window.fbq('track', 'Lead');
  }
};

/**
 * WhatsApp click-to-chat button pressed.
 * @param source the page path the click happened on (per-page conversion view)
 */
export const trackWhatsAppClick = (source?: string): void => {
  trackEvent('whatsapp_click', { channel: 'whatsapp', source });
};

/**
 * Click-to-call button pressed.
 * @param source the page path the click happened on
 */
export const trackCallClick = (source?: string): void => {
  trackEvent('call_click', { channel: 'phone', source });
};

/**
 * Newsletter subscribe request accepted (double opt-in pending). Fired when the
 * signup POST succeeds, before email confirmation. Also reports a Pixel `Lead`.
 * @param source where the signup happened (e.g. a blog slug)
 */
export const trackNewsletterSignup = (source: string): void => {
  trackEvent('newsletter_signup', { source });
  if (pixelEnabled() && window.fbq) {
    window.fbq('track', 'Lead');
  }
};

/**
 * In-article "Request a demo" call-to-action clicked. Measures how well blog
 * content drives readers toward the contact/demo funnel.
 * @param source which article the CTA lives in (e.g. a blog slug)
 */
export const trackBlogCtaClick = (source: string): void => {
  trackEvent('blog_cta_click', { source });
};

/**
 * Pricing-page call-to-action clicked, the highest-intent signal on the
 * marketing site.
 * @param plan which CTA: "monthly", "annual" or "bottom"
 */
export const trackPricingCtaClick = (plan: string): void => {
  trackEvent('pricing_cta_click', { plan });
};
